import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCopy, faPaperPlane, faMobileButton, faPhone, faClose, faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DialogClose,
  DialogHeader,
  DialogTrigger,
  Dialog,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  DialogContent,
  DialogTitle,
  DialogFooter,
  Button,
  DialogDescription,
  Input,
  Textarea,
  PhoneInput,
  toast,
} from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import constants from '@playcastdotio/GlobalConfig';
import { generateQRCode } from '@playcastdotio/Qrcode';
import { InvitationSendState } from '@playcastdotio/CoreTypes';
import { sendAnalytics, PlaycastAnalyticsInviteCreated } from '@playcastdotio/Analytics';

const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const isValidEmail = (email: string): boolean => {
  const regexPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regexPattern.test(email);
};
const isValidPhoneNumber = (phone: string): boolean => {
  // Starts with +, followed by numbers, spaces, dashes, or parentheses
  const regexPattern = /^\+[\d\s\-()]+$/;
  return regexPattern.test(phone);
};

const formatPhoneNumber = (phone: string): string => {
  // Remove spaces, dashes, and parentheses
  const cleanedPhone = phone.replace(/[\s\-()]/g, '');
  return cleanedPhone;
};

type Props = {
  icon: IconProp;
  text: string;
  className?: string;
  invitationId: string;
  invitationSendState: InvitationSendState;
  onCopy: (invitationId: string) => void;
  onSendEmail: (invitationId: string, target: string, message: string) => void;
  onSendSms: (invitationId: string, target: string, message: string) => void;
  authId: string;
};

export const InviteButton = ({ icon, text, className, invitationId, onCopy, onSendEmail, onSendSms, invitationSendState, authId }: Props) => {
  //   const context = useContext(PlaycastContext);
  const [show, setShow] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleCopyClicked = () => {
    setShow(false);
    onCopy(invitationId);
    // const streamUrl = `${context.stateStores.hostState.streamBaseUrl}j/${context.stateStores.invitationStateV2.id}`;
    // if (context.electronFunctions) {
    //   context.electronFunctions.copyToClipboard(streamUrl);
    // } else {
    //   navigator.clipboard.writeText(streamUrl);
    // }
  };

  const handleSendEmail = (target: string, message: string) => {
    // Send analytics?
    sendAnalytics({
      name: 'Invite Created',
      role: 'host',
      inviteType: 'email',
      hostAuthId: authId,
      invitationId: invitationId,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsInviteCreated);

    onSendEmail(invitationId, target, message);
    // context.targetFunctions.sendInvitationEmail(target, message);
  };

  const handleSendSms = (target: string, message: string) => {
    // Send analytics?
    sendAnalytics({
      name: 'Invite Created',
      role: 'host',
      inviteType: 'sms',
      hostAuthId: authId,
      invitationId: invitationId,
      tier2Filters: ['machine'],
    } as PlaycastAnalyticsInviteCreated);
    
    onSendSms(invitationId, target, message);
    // context.targetFunctions.sendInvitationSms(target, message);
  };

  return (
    <div className='relative flex w-full flex-col gap-2' data-testid='share-invite-button'>
      <div
        className={clsx(
          disableButton ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          'border-primary hover:border-primary/80 flex h-12 w-full items-center justify-center rounded-lg border-2 bg-inherit p-2 text-white',
          className,
        )}
        // onClick={() => {
        //   if (disableButton) return;
        //   setShow((prev) => !prev);
        // }}
        onMouseDown={() => {
          if (disableButton) return;
          setShow((prev) => !prev);
          setTimeout(() => {
            dropdownRef.current?.focus();
          }, 0);
        }}
      >
        <div className='flex flex-1 items-center justify-center text-center'>
          <FontAwesomeIcon icon={icon} className='!text-amber-200' />
          <span className={clsx(disableButton ? 'cursor-not-allowed' : 'cursor-pointer', 'pl-2 font-semibold')}>{text}</span>
        </div>
        {/* <FontAwesomeIcon icon={faCaretDown} className='h-3 w-3 transition-all' /> */}
      </div>
      <div
        ref={dropdownRef}
        tabIndex={0}
        onBlur={() => setShow(false)}
        className={clsx(
          'top-12',
          'absolute z-10 my-2 flex min-w-[180px] flex-col gap-2 rounded-md bg-gray-300 p-2 text-gray-900 shadow',
          show ? 'block' : 'hidden',
        )}
      >
        <button className='group flex items-center' onMouseDown={handleCopyClicked} data-testid='share-copy-link-button'>
          <div
            className='group-hover:border-midnight-bright flex w-full cursor-pointer gap-2 rounded-md border-2 border-gray-300 p-2 text-left text-sm font-normal group-hover:border-dashed'
            data-testid='share-copy-link-button'
          >
            <FontAwesomeIcon icon={faCopy} />
            <Label className='cursor-pointer'>Copy Link</Label>
          </div>
        </button>
        <InviteModule
          title='SMS Invite'
          buttonText='SMS'
          buttonIcon={faMobileButton}
          placeholder='Enter Phone Number'
          onSend={handleSendSms}
          type='sms'
          invitationId={invitationId}
          invitationSendState={invitationSendState}
        />
        <InviteModule
          title='Email Invite'
          buttonText='Email'
          buttonIcon={faPaperPlane}
          placeholder='email@domain.com'
          onSend={handleSendEmail}
          type='email'
          invitationId={invitationId}
          invitationSendState={invitationSendState}
        />
      </div>
    </div>
  );
};

type InviteModuleProps = {
  title: string;
  buttonText: string;
  buttonIcon: IconProp;
  placeholder: string;
  onSend: (target: string, message: string) => void;
  type: 'sms' | 'email';
  invitationId: string;
  invitationSendState: InvitationSendState;
};

const InviteModule = ({
  title,
  buttonText,
  buttonIcon,
  placeholder,
  onSend,
  type,
  invitationId,
  invitationSendState,
}: InviteModuleProps) => {
  const defaultMessage = "You're invited to join this Playcast session";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [disableButton, setDisableButton] = useState(false);

  const [validTarget, setValidTarget] = useState(true);
  const [limitExceeded, setLimitExceeded] = useState(false);
  const [remainingInvites, setRemainingInvites] = useState(0);
  const [validMessage, setValidMessage] = useState(true);

  const [target, setTarget] = useState('');
  const [message, setMessage] = useState(defaultMessage);

  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [qrCodeLink, setQrCodeLink] = useState<string>('');

  useEffect(() => {
    const streamUrl = constants.PAVILION_PLAYER_BASE_URL + 'j/' + invitationId;
    setQrCodeUrl(streamUrl);
    setQrCodeLink(streamUrl);
  }, [invitationId]);

  useEffect(() => {
    const generateQR = async () => {
      await generateQRCode(qrCodeLink, { logo: '/icon.png' })
        .then((svgString: string | null | undefined) => {
          if (svgString) {
            setQrCodeUrl(svgString);
          }
        })
        .catch((error) => {
          console.error('Failed to generate QR code:', error);
        });
    };

    if (qrCodeLink) {
      generateQR();
    }
  }, [qrCodeLink]);

  // Clean up the blob URL when the component unmounts to avoid memory leaks
  useEffect(() => {
    return () => {
      if (qrCodeUrl) {
        URL.revokeObjectURL(qrCodeUrl);
      }
    };
  }, [qrCodeUrl]);

  useEffect(() => {
    if (type === 'email') {
      setLimitExceeded(invitationSendState.reachedEmailInviteLimit);
      const emailCount = invitationSendState.emailCount;
      const emailLimit = invitationSendState.emailLimit;

      setRemainingInvites(emailLimit - emailCount);
    } else if (type === 'sms') {
      setLimitExceeded(invitationSendState.smsCount >= invitationSendState.smsLimit);
      const smsCount = invitationSendState.smsCount;
      const smsLimit = invitationSendState.smsLimit;

      setRemainingInvites(smsLimit - smsCount);
    }
    if (limitExceeded) {
      // setFormError(`You've reached the limit of 10 ${type === 'email' ? 'Email' : 'Sms'} invites for today. Please try again later.`);
    }
  }, [type, limitExceeded, invitationSendState]);

  const handleTargetChanged = (value: string | React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = '';
    if (typeof value === 'string') {
      inputValue = value;
    } else {
      if (value === null || value === undefined) {
        inputValue = '';
      } else {
        inputValue = value.target.value;
      }
    }
    let valid = false;

    if (type === 'email') {
      valid = isValidEmail(inputValue);
      setTarget(inputValue);
    } else if (type === 'sms') {
      valid = isValidPhoneNumber(inputValue);
      const formattedTarget = formatPhoneNumber(inputValue);
      setTarget(formattedTarget);
    }
    setValidTarget(valid);
    if (valid) {
      if (!validMessage) {
        // setFormError(targetInputErrorMessage);
      } else {
        // setFormError('');
      }
      return;
    } else {
      if (!validMessage) {
        // setFormError(`${targetInputErrorMessage} and ${messageInputErrorMessage}`);
      } else {
        // setFormError(targetInputErrorMessage);
      }
    }
  };

  const handleMessageChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const messageLength = e.target.value.length;
    if (messageLength > 200) {
      setValidMessage(false);
      if (!validTarget) {
        // setFormError(`${targetInputErrorMessage} and ${messageInputErrorMessage}`);
      } else {
        // setFormError(messageInputErrorMessage);
      }
      return;
    }
    setValidMessage(true);
    if (!validTarget) {
      // setFormError(targetInputErrorMessage);
    } else {
      // setFormError('');
    }
    setMessage(e.target.value);
  };

  useEffect(() => {
    setMessage(defaultMessage);
    setTarget('');
  }, []);

  const handleClicked = () => {
    if (!validMessage || !validTarget) return;
    onSend(target, message);
    setTarget('');
    setMessage('');
    // setFormError('');
    setValidMessage(true);
    setValidTarget(true);
    setDialogOpen(false);
  };

  const handleDialogOpen = () => {
    if (disableButton) return;
    setDialogOpen(true);
  };

  return (
    <Dialog open={dialogOpen}>
      <DialogTrigger asChild>
        <button
          className='group flex items-center'
          onMouseDown={(e) => {
            e.stopPropagation();
            handleDialogOpen();
          }}
        >
          <div className='group-hover:border-midnight-bright flex w-full cursor-pointer gap-2 rounded-md border-2 border-gray-300 p-2 text-left text-sm font-normal group-hover:border-dashed'>
            <FontAwesomeIcon icon={buttonIcon} />
            <Label className='cursor-pointer'>{buttonText}</Label>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className='border-none bg-transparent text-center sm:max-w-[425px]' onPointerDownOutside={() => setDialogOpen(false)}>
        <DialogHeader>
          <DialogTitle className="text-primary text-center font-['Syne'] text-2xl">{title}</DialogTitle>
          <DialogDescription className='text-center text-white'></DialogDescription>
        </DialogHeader>
        {(type === 'email' || (type === 'sms' && !limitExceeded)) && (
          <>
            <DialogDescription className='text-center text-white'>
              {type === 'email' ? `Send an email invite (${remainingInvites} left)` : `Send an SMS invite (${remainingInvites} left)`}
            </DialogDescription>
            <div className='flex flex-col gap-y-4 text-white'>
              <div className='relative items-center'>
                {type === 'email' ? (
                  <Input
                    type='text'
                    placeholder={placeholder}
                    value={target}
                    disabled={limitExceeded}
                    className={clsx(validTarget ? 'border-emerald-600' : 'border-red-600', 'pr-10')}
                    onChange={handleTargetChanged}
                  />
                ) : (
                  <PhoneInput
                    className={clsx(validTarget ? 'border-emerald-600' : 'border-red-600', '')}
                    placeholder={placeholder}
                    value={target}
                    disabled={limitExceeded}
                    onChange={handleTargetChanged}
                    defaultCountry='US'
                  />
                )}
                <FontAwesomeIcon
                  icon={type === 'sms' ? faPhone : faEnvelope}
                  className='absolute right-4 top-5 -translate-y-1/2 transform'
                />
              </div>
              <Textarea
                placeholder='Custom message (0/200)'
                value={message}
                disabled={limitExceeded}
                className={validMessage ? 'border-emerald-600' : 'border-red-600'}
                onChange={handleMessageChanged}
              />
            </div>
            <DialogFooter>
              <DialogClose asChild className='w-full'>
                <Button
                  type='button'
                  className='m-0 w-full gap-x-2 bg-gray-600 text-white hover:bg-gray-700 focus:bg-gray-500 active:bg-gray-500'
                  onClick={() => {
                    setTarget('');
                    setMessage('');
                    setDialogOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faClose} />
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type='submit'
                className='m-0 w-full gap-x-2 bg-emerald-600 text-white hover:bg-emerald-700 focus:bg-emerald-500 active:bg-emerald-500'
                disabled={!validMessage || !validTarget || limitExceeded}
                onClick={handleClicked}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Send
              </Button>
            </DialogFooter>
          </>
        )}

        {type === 'sms' && limitExceeded && <QRMessage image={qrCodeUrl} qrCodeLink={qrCodeLink} />}
      </DialogContent>
    </Dialog>
  );
};

const QRMessage = ({ image, qrCodeLink }: { image: string; qrCodeLink: string }) => {
  return (
    <div className='flex flex-col items-center gap-4'>
      <Label className='text-md text-amber-400'>Out of daily app messages</Label>
      <Label className='text-amber-400'>Scan this code to message your friends.</Label>
      <img src={image} alt='QR Code' className='mx-auto h-64 w-64' />
      {isTouchDevice() ? (
        <Link className='w-fit italic text-amber-400 underline' to={qrCodeLink}>
          Or tap this link
        </Link>
      ) : (
        ''
      )}
    </div>
  );
};
